/* =====================================================================
 * rehype-temml — rehype plugin that renders math with Temml (MathML)
 *
 * Drop-in replacement for rehype-katex.
 * Uses the same remark-math AST classes (math-display, math-inline,
 * language-math) but renders with Temml instead of KaTeX.
 *
 * Temml 장점:
 *  - 번들 크기 ~60% 감소 (KaTeX 600KB → Temml 350KB)
 *  - MathML 출력 → 브라우저 네이티브 렌더링, 접근성 향상
 *  - CSS 크기 ~66% 감소 (24KB → 8KB)
 * ===================================================================== */

import { fromHtmlIsomorphic } from 'hast-util-from-html-isomorphic';
import { toText } from 'hast-util-to-text';
import { SKIP, visitParents } from 'unist-util-visit-parents';
import temml from 'temml';
import type { Options as TemmlOptions } from 'temml';

import type { ElementContent, Root, Element } from 'hast';
import type { VFile } from 'vfile';

export interface Options {
  /** Temml 옵션 — displayMode & throwOnError 는 플러그인이 제어 */
  annotate?: boolean;
  leqno?: boolean;
  errorColor?: string;
  macros?: Record<string, unknown>;
  wrap?: 'none' | 'tex' | '=';
  xml?: boolean;
  colorIsTextColor?: boolean;
  strict?: boolean;
  trust?: boolean | ((context: unknown) => boolean);
  maxSize?: [number, number];
  maxExpand?: number;
}

const emptyOptions: Readonly<Options> = {};
const emptyClasses: ReadonlyArray<unknown> = [];

/**
 * Render elements with a `language-math` (or `math-display`, `math-inline`)
 * class with Temml (MathML output).
 */
export default function rehypeTemml(options?: Readonly<Options> | null | undefined) {
  const settings = options || emptyOptions;

  return function (tree: Root, file: VFile): undefined {
    visitParents(tree, 'element', function (element: Element, parents) {
      const classes = Array.isArray(element.properties.className)
        ? element.properties.className
        : emptyClasses;

      const languageMath = classes.includes('language-math');
      const mathDisplay = classes.includes('math-display');
      const mathInline = classes.includes('math-inline');
      let displayMode = mathDisplay;

      if (!languageMath && !mathDisplay && !mathInline) {
        return;
      }

      let parent = parents[parents.length - 1] as Element;
      let scope: Element = element;

      // ` ```math ` 코드블록 → <pre> 를 교체, display mode
      if (
        element.tagName === 'code' &&
        languageMath &&
        parent &&
        parent.type === 'element' &&
        parent.tagName === 'pre'
      ) {
        scope = parent;
        parent = parents[parents.length - 2] as Element;
        displayMode = true;
      }

      if (!parent) return;

      const value = toText(scope, { whitespace: 'pre' });

      let result: Array<ElementContent> | string | undefined;

      try {
        result = temml.renderToString(value, {
          ...settings,
          displayMode,
          throwOnError: true,
        } as TemmlOptions);
      } catch (error) {
        const cause = error as Error;
        const ruleId = cause.name.toLowerCase();

        file.message('Could not render math with Temml', {
          ancestors: [...parents, element],
          cause,
          place: element.position,
          ruleId,
          source: 'rehype-temml',
        });

        // 에러 시 throwOnError: false 로 재시도
        try {
          result = temml.renderToString(value, {
            ...settings,
            displayMode,
            throwOnError: false,
          } as TemmlOptions);
        } catch {
          // 최종 폴백: 원본 LaTeX 표시
          result = [
            {
              type: 'element',
              tagName: 'span',
              properties: {
                className: ['temml-error'],
                style: 'color:' + (settings.errorColor || '#cc0000'),
                title: String(error),
              },
              children: [{ type: 'text', value }],
            },
          ];
        }
      }

      if (typeof result === 'string') {
        const root = fromHtmlIsomorphic(result, { fragment: true });
        result = root.children as Array<ElementContent>;
      }

      const index = parent.children.indexOf(scope);
      parent.children.splice(index, 1, ...(result as ElementContent[]));
      return SKIP;
    });
  };
}

